import React, { forwardRef } from 'react';
import { useRestaurant } from '../context/RestaurantContext';
import { CheckoutData, CartItemType } from '../types/types';
import { useLanguage } from '../context/LanguageContext';
interface OrderTicketProps {
  orderNumber: string;
  data: CheckoutData;
  items: CartItemType[];
  cartTotal: number;
  totalFinal: number;
}
export const OrderTicket = forwardRef<HTMLDivElement, OrderTicketProps>(({
  orderNumber,
  data,
  items,
  cartTotal,
  totalFinal
}, ref) => {
  const {
    restaurant
  } = useRestaurant();
  const appName = restaurant?.name || '';
  const logoUrl = restaurant?.logo_url || '';
  const deliveryFee = restaurant?.delivery_fee || 0;
  const {
    t,
    language,
    formatPrice,
    translateProductName,
    translateIngredient,
    translateSelectionLabel
  } = useLanguage();
  const cartTotalFormatted = formatPrice(cartTotal);
  const expressFeeFormatted = formatPrice(deliveryFee);
  const totalFinalFormatted = formatPrice(totalFinal);
  // Use language-aware date formatting
  const dateLocale = language === 'es' ? 'es-CR' : language === 'fr' ? 'fr-FR' : language === 'zh' ? 'zh-CN' : language === 'pt' ? 'pt-BR' : language === 'de' ? 'de-DE' : 'en-US';
  return <div ref={ref} className="bg-surface w-[340px] p-6 font-mono text-sm border border-gray-800 shadow-2xl relative overflow-hidden" data-id="element-1035">
        {/* Ticket zig-zag top edge (simulated with CSS) */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-repeat-x bg-[length:10px_10px] bg-[radial-gradient(circle_at_50%_0,transparent_5px,#1A1A1A_6px)]" data-id="element-1036" />

        {/* Header */}
        <div className="text-center mb-6 pt-2" data-id="element-1037">
          <div className="w-16 h-16 mx-auto mb-3 grayscale contrast-200" data-id="element-1038">
            <img src={logoUrl} alt={appName} className="w-full h-full object-contain rounded-full border border-gray-700" data-id="element-1039" />
          </div>
          <h1 className="font-bold text-xl text-white tracking-widest uppercase" data-id="element-1040">
            {appName}
          </h1>
          <p className="text-xs text-gray-400 mt-1 uppercase tracking-wider" data-id="element-1041">
            {t('tagline')}
          </p>
          <div className="border-b-2 border-dashed border-gray-700 my-4" data-id="element-1042" />
          <p className="font-bold text-lg text-white" data-id="element-1043">
            {t('orderNumber')} #{orderNumber}
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-1044">
            {new Date().toLocaleDateString(dateLocale, {
          weekday: 'short',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}
          </p>
        </div>

        {/* Customer Info */}
        <div className="mb-6 space-y-1 text-gray-300" data-id="element-1045">
          <p data-id="element-1046">
            <span className="font-semibold text-white" data-id="element-1047">{t('client')}:</span>{' '}
            {data.nombre}
          </p>
          <p data-id="element-1048">
            <span className="font-semibold text-white" data-id="element-1049">{t('tel')}:</span>{' '}
            {data.telefono}
          </p>
          <p data-id="element-1050">
            <span className="font-semibold text-white" data-id="element-1051">{t('delivery')}:</span>{' '}
            {data.entrega === 'Retiro en local' ? t('pickupLocal') : t('express')}
          </p>
          <p data-id="element-1052">
            <span className="font-semibold text-white" data-id="element-1053">{t('payment')}:</span>{' '}
            {data.metodoPago === 'Tarjeta' ? t('card') : data.metodoPago === 'Efectivo' ? t('cash') : data.metodoPago}
          </p>
        </div>

        <div className="border-b-2 border-dashed border-gray-700 mb-4" data-id="element-1054" />

        {/* Items */}
        <div className="mb-6 space-y-4" data-id="element-1055">
          <div className="flex justify-between font-bold text-white mb-2 pb-2 border-b border-gray-800" data-id="element-1056">
            <span data-id="element-1057">
              {t('qty')} {t('description')}
            </span>
            <span data-id="element-1058">{t('total')}</span>
          </div>

          {items.map((item, idx) => <div key={idx} className="text-gray-300" data-id="element-1059">
              <div className="flex justify-between font-bold text-white" data-id="element-1060">
                <span data-id="element-1061">
                  {item.quantity}x{' '}
                  {translateProductName(item.productId, item.name)}
                </span>
                <span data-id="element-1062">
                  {formatPrice(item.subtotal * item.quantity).primary}
                </span>
              </div>

              <div className="pl-4 text-xs mt-1 space-y-0.5 text-gray-400" data-id="element-1063">
                {item.selections.base && <p data-id="element-1064">
                    {translateSelectionLabel('base')}:{' '}
                    {translateIngredient(item.selections.base.id, item.selections.base.name)}
                  </p>}
                {item.selections.proteina && <p data-id="element-1065">
                    {translateSelectionLabel('proteina')}:{' '}
                    {translateIngredient(item.selections.proteina.id, item.selections.proteina.name)}
                  </p>}
                {item.selections.toppings && item.selections.toppings.length > 0 && <p data-id="element-1066">
                      {t('toppings')}:{' '}
                      {item.selections.toppings.map(tp => translateIngredient(tp.id, tp.name)).join(', ')}
                    </p>}
                {item.selections.toppingsPremium && item.selections.toppingsPremium.length > 0 && <p data-id="element-1067">
                      {translateSelectionLabel('premium')}:{' '}
                      {item.selections.toppingsPremium.map(tp => translateIngredient(tp.id, tp.name)).join(', ')}
                    </p>}
                {item.selections.aderezos && item.selections.aderezos.length > 0 && <p data-id="element-1068">
                      {t('dressing')}:{' '}
                      {item.selections.aderezos.map(ad => translateIngredient(ad.id, ad.name)).join(', ')}
                    </p>}
                {item.selections.semillas && item.selections.semillas.length > 0 && <p data-id="element-1069">
                      {translateSelectionLabel('seeds')}:{' '}
                      {item.selections.semillas.map(sd => translateIngredient(sd.id, sd.name)).join(', ')}
                    </p>}
                {item.selections.comentarios && <p className="italic mt-1" data-id="element-1070">
                    {t('note')}: {item.selections.comentarios}
                  </p>}
              </div>
            </div>)}
        </div>

        <div className="border-b-2 border-dashed border-gray-700 mb-4" data-id="element-1071" />

        {/* Totals */}
        <div className="space-y-1 mb-6 text-gray-300" data-id="element-1072">
          <div className="flex justify-between" data-id="element-1073">
            <span data-id="element-1074">{t('subtotal')}</span>
            <span data-id="element-1075">{cartTotalFormatted.primary}</span>
          </div>
          {data.entrega === 'Express' && <div className="flex justify-between" data-id="element-1076">
              <span data-id="element-1077">{t('express')}</span>
              <span data-id="element-1078">{expressFeeFormatted.primary}</span>
            </div>}
          <div className="flex justify-between font-bold text-lg pt-2 mt-2 border-t border-gray-800 text-white" data-id="element-1079">
            <span data-id="element-1080">{t('total')}</span>
            <span data-id="element-1081">{totalFinalFormatted.primary}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-gray-500 mt-8" data-id="element-1082">
          <p className="font-bold text-gray-400 mb-1" data-id="element-1083">{t('thankYou')}</p>
          <p data-id="element-1084">{t('followUs')}</p>
          <p data-id="element-1085">@{appName.replace(/\s+/g, '').toLowerCase()}</p>
        </div>

        {/* Ticket zig-zag bottom edge */}
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-repeat-x bg-[length:10px_10px] bg-[radial-gradient(circle_at_50%_10px,transparent_5px,#1A1A1A_6px)]" data-id="element-1086" />
      </div>;
});